<template>
  <div>
    <v-card outlined>
      <v-card-title class="grey lighten-3">
        <slot name="title">{{ title }}</slot>
        <v-spacer></v-spacer>
        <slot name="headerButtons"></slot>
      </v-card-title>

      <v-row class="px-2 py-1 mt-2 font-weight-light">
        <v-col cols="12" sm="2">
          <date-picker
            class="mx-2"
            label="From Date"
            v-model="search.fromDate"
            @input="searchUpdated"
            outlined
            dense
          />
        </v-col>
        <v-col cols="12" sm="2">
          <date-picker
            label="To Date"
            v-model="search.toDate"
            @input="searchUpdated"
            outlined
            dense
          />
        </v-col>
      </v-row>

      <v-divider class="my-4"></v-divider>

      <v-card-text>
        <v-data-table
          :headers="headersTable"
          :items="logs"
          dense
          :footer-props="{ 'items-per-page-options': [15, 50, 100, -1] }"
          :items-per-page="50"
          :options.sync="options"
          :server-items-length="totalLogs"
          multi-sort
          :loading="loading"
          show-expand
          single-expand
          v-model:expanded="expanded"
          item-key="id"
        >
          <!-- Slot voor Log Time kolom -->
          <template #item.durationMs="{ item }">
            <span
              :class="{
                'red--text font-weight-bold': item.durationMs >= 5,
              }"
            >
              {{ Number(item.durationMs) }} s
            </span>
          </template>

          <!-- Slot voor expanded row -->
          <template #expanded-item="{ headers, item }">
            <td :colspan="headers.length">
              <LogDetails :log="item" />
            </td>
          </template>
          <!-- Header slots -->
          <template v-slot:header="{ props: { headers } }">
            <tr v-show="$vuetify.breakpoint.smAndUp">
              <th v-for="header in headers" :key="header.value">
                <div
                  v-if="header.type === 'text'"
                  class="mx-2 mt-6 font-weight-light"
                >
                  <v-text-field
                    dense
                    :label="header.text"
                    clearable
                    v-model="search[header.value]"
                    @input="searchUpdated"
                    outlined
                  />
                </div>
                <div
                  v-else-if="header.type === 'date'"
                  class="mx-2 font-weight-light"
                >
                  <date-picker
                    dense
                    :label="header.text"
                    v-model="search[header.value]"
                    @input="searchUpdated"
                    outlined
                  />
                </div>
                <div
                  v-else-if="header.type === 'select'"
                  class="mx-2 mt-6 font-weight-light"
                >
                  <v-select
                    dense
                    :label="header.text"
                    v-model="search[header.value]"
                    :items="headerSelects[header.value]"
                    item-text="text"
                    item-value="value"
                    @change="searchUpdated"
                    outlined
                    clearable
                  />
                </div>
                <div
                  v-else-if="header.type === 'multiselect'"
                  class="mx-2 font-weight-light"
                >
                  <v-select
                    dense
                    :label="header.text"
                    v-model="search[header.value]"
                    :items="headerSelects[header.value]"
                    @change="searchUpdated"
                    outlined
                    clearable
                    multiple
                  />
                </div>
              </th>
            </tr>

            <!-- XS only -->
            <tr
              v-show="$vuetify.breakpoint.xsOnly"
              v-for="header in headers"
              :key="header.value"
            >
              <th>
                <div
                  v-if="header.type === 'text'"
                  class="mx-2 font-weight-light"
                >
                  <v-text-field
                    dense
                    :label="header.text"
                    clearable
                    v-model="search[header.value]"
                    @input="searchUpdated"
                    outlined
                  />
                </div>
                <div
                  v-else-if="header.type === 'select'"
                  class="mx-2 font-weight-light"
                >
                  <v-select
                    dense
                    :label="header.text"
                    v-model="search[header.value]"
                    :items="headerSelects[header.value]"
                    @change="searchUpdated"
                    outlined
                    clearable
                  />
                </div>
                <div
                  v-else-if="header.type === 'date'"
                  class="mx-2 font-weight-light"
                >
                  <date-picker
                    dense
                    :label="header.text"
                    v-model="search[header.value]"
                    @input="searchUpdated"
                    outlined
                  />
                </div>
              </th>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import _ from "lodash";
import LogDetails from "./LogDetails.vue";
import DatePicker from "@/components/common/DatePicker.vue";

export default {
  name: "DataTable",
  props: {
    title: {
      typeof: String,
      default: "Search Results",
    },
    optionsPassed: {
      typeof: Object,
      required: true,
    },
    totalLogs: {
      typeof: Number,
      required: true,
    },
    logs: {
      typeof: Array,
      required: true,
    },
    searchPassed: {
      typeof: Array,
      required: true,
    },
    headerSelects: {
      typeof: Array,
      required: true,
    },
    headersTable: {
      typeof: Array,
      required: true,
    },
    loading: Boolean,
    routeToDetails: {
      typeof: Boolean,
      default: true,
    },
  },
  components: {
    LogDetails,
    DatePicker,
  },
  data() {
    return {
      expanded: [],
      expandedLog: null,
      // loading: false,
      searchTimeOut: null,
      options: this.optionsPassed,
      search: this.searchPassed,
    };
  },
  methods: {
    getOrganisations() {
      if (
        !this.headerSelects["organisation"] ||
        this.headerSelects["organisation"].length === 0
      ) {
        this.$api
          .getOrganisations()
          .then((resp) => {
            console.log("Organisations fetched:", resp.data);
            const orgs = resp.data
              .filter((o) => o.text)
              .filter((o) => o.value > 0)
              .map((o) => ({
                text: o.text,
                value: o.value,
              }));

            this.$set(this.headerSelects, "organisation", orgs);
          })
          .catch((err) => {
            console.error("Error fetching organisations:", err);
          });
      }
    },
    async getCountries() {
      if (
        !this.headerSelects["country"] ||
        this.headerSelects["country"].length === 0
      ) {
        try {
          const resp = await this.$api.getCountries();

          const countries = resp.data.map((c) => ({
            text: `${c.text} (${c.iso2Code})`,
            value: c.iso2Code,
          }));
          this.$set(this.headerSelects, "country", countries);
        } catch (err) {
          console.error("Error fetching countries:", err);
        }
      }
    },
    getSBMProfiles() {
      if (
        !this.headerSelects["sbmProfile"] ||
        this.headerSelects["sbmProfile"].length === 0
      ) {
        this.$api
          .getSBMProfiles()
          .then((resp) => {
            this.$set(this.headerSelects, "sbmProfile", resp.data);
          })
          .catch((err) => {
            console.error("Error fetching sbmProfiles:", err);
          });
      }
    },
    searchUpdated: _.throttle(function () {
      let sendSearch = {
        options: this.options,
        search: this.search,
        ignoreServiceLimitTime: !this.showServiceLimitTime,
      };
      this.$emit("getECareApiLogs", sendSearch);
    }, 500),
  },
  mounted() {
    this.getOrganisations();
    this.getCountries();
    this.getSBMProfiles();
  },
  watch: {
    options: {
      handler() {
        this.searchUpdated();
      },
      deep: true,
    },
  },
};
</script>